import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { apiFetch, requireOk } from "../lib/api";
import { trackWorkflow } from "../lib/telemetry";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const APP_VERSION = typeof import.meta !== "undefined" && import.meta.env?.VITE_APP_VERSION
  ? import.meta.env.VITE_APP_VERSION
  : "dev";

export async function submitFeedback({ kind, message, stack, route }) {
  const res = await apiFetch("/feedback", {
    method: "POST",
    body: JSON.stringify({
      kind,
      message,
      stack: stack || undefined,
      route: route || (typeof window !== "undefined" ? window.location.pathname : undefined),
      app_version: APP_VERSION,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    }),
  });
  await requireOk(res, "Could not submit report");
  trackWorkflow("feedback_submit", { kind });
  return res.json();
}

export default function ReportProblemDialog({ open, onOpenChange }) {
  const location = useLocation();
  const [kind, setKind] = useState("feedback");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please describe the issue");
      return;
    }
    setSending(true);
    try {
      await submitFeedback({
        kind,
        message: message.trim(),
        route: location.pathname,
      });
      toast.success("Thanks — report sent to Rygan");
      setMessage("");
      setKind("feedback");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Could not submit report");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="report-problem-dialog">
        <DialogHeader>
          <DialogTitle>Report a problem</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="report-kind">Type</Label>
            <select
              id="report-kind"
              className="w-full h-9 rounded-md border border-brand-border bg-brand-panel px-2 text-sm "
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              data-testid="report-kind"
            >
              <option value="feedback">Feedback</option>
              <option value="bug">Bug</option>
              <option value="crash">Crash / blank screen</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="report-message">What happened?</Label>
            <Textarea
              id="report-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={8000}
              placeholder="Steps, what you expected, what you saw…"
              data-testid="report-message"
            />
          </div>
          <p className="text-[11px] text-brand-steel">
            Route and browser info are included. Do not paste passwords or API keys.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={sending} data-testid="report-submit">
              {sending ? "Sending…" : "Send report"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
